import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionSection = () => {
  const [currentPlan] = useState({
    name: 'Dealer Pro',
    price: 999,
    billingCycle: 'monthly',
    status: 'active',
    nextBilling: '2025-12-06',
    trialEndsAt: null
  });

  const [usage] = useState({
    valuations: { used: 847, limit: 'unlimited' },
    reports: { used: 234, limit: 'unlimited' },
    bulkUploads: { used: 12, limit: 50 },
    apiCalls: { used: 15420, limit: 25000 }
  });

  const [billingHistory] = useState([
    {
      id: 1,
      date: '2025-11-06',
      amount: 999,
      status: 'paid',
      description: 'Dealer Pro - Monthly Subscription',
      invoiceUrl: '#'
    },
    {
      id: 2,
      date: '2025-10-06',
      amount: 999,
      status: 'paid',
      description: 'Dealer Pro - Monthly Subscription',
      invoiceUrl: '#'
    },
    {
      id: 3,
      date: '2025-09-06',
      amount: 999,
      status: 'paid',
      description: 'Dealer Pro - Monthly Subscription',
      invoiceUrl: '#'
    }
  ]);

  const handleManageSubscription = () => {
    // This would typically redirect to Stripe Customer Portal
    console.log('Opening Stripe Customer Portal...');
    window.open('https://billing.stripe.com/p/login/test_mock_portal', '_blank');
  };

  const handleUpgradePlan = () => {
    console.log('Upgrading to Lender Pro...');
  };

  const handleDownloadInvoice = (invoiceUrl) => {
    console.log('Downloading invoice:', invoiceUrl);
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === 'unlimited') return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'past_due': return 'text-warning';
      case 'canceled': return 'text-destructive';
      default: return 'text-text-secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'CheckCircle';
      case 'past_due': return 'AlertTriangle';
      case 'canceled': return 'XCircle';
      default: return 'Clock';
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="CreditCard" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-text-primary">Current Plan</h3>
          </div>
          <div className={`flex items-center space-x-2 ${getStatusColor(currentPlan?.status)}`}>
            <Icon name={getStatusIcon(currentPlan?.status)} size={16} />
            <span className="text-sm font-medium capitalize">{currentPlan?.status}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-xl font-semibold text-text-primary">{currentPlan?.name}</h4>
                <p className="text-text-secondary">
                  ${currentPlan?.price?.toLocaleString()}/{currentPlan?.billingCycle}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-text-secondary">Next billing</p>
                <p className="font-medium text-text-primary">
                  {new Date(currentPlan.nextBilling)?.toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="Check" size={14} className="text-success" />
                <span>Unlimited crane valuations</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="Check" size={14} className="text-success" />
                <span>Professional PDF reports</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="Check" size={14} className="text-success" />
                <span>Inventory alerts & monitoring</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="Check" size={14} className="text-success" />
                <span>Bulk CSV upload (50/month)</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              variant="default"
              onClick={handleManageSubscription}
              iconName="ExternalLink"
              iconPosition="right"
              fullWidth
            >
              Manage Subscription
            </Button>
            <Button
              variant="outline"
              onClick={handleUpgradePlan}
              iconName="ArrowUp"
              iconPosition="left"
              fullWidth
            >
              Upgrade to Lender Pro
            </Button>
          </div>
        </div>
      </div>
      {/* Usage Dashboard */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="BarChart3" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">Usage Dashboard</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(usage)?.map(([key, data]) => (
            <div key={key} className="bg-muted rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-text-primary capitalize">
                  {key?.replace(/([A-Z])/g, ' $1')?.trim()}
                </h4>
                <span className="text-xs text-text-secondary">
                  {data?.limit === 'unlimited' ? '∞' : `/${data?.limit}`}
                </span>
              </div>
              
              <div className="mb-2">
                <span className="text-2xl font-bold text-text-primary">
                  {data?.used?.toLocaleString()}
                </span>
              </div>
              
              {data?.limit !== 'unlimited' && (
                <div className="w-full bg-border rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${getUsagePercentage(data?.used, data?.limit)}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Billing History */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="Receipt" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-text-primary">Billing History</h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Download All
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Date</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Description</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Amount</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Status</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {billingHistory?.map((invoice) => (
                <tr key={invoice?.id} className="border-b border-border">
                  <td className="py-3 px-4 text-sm text-text-primary">
                    {new Date(invoice.date)?.toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-text-primary">
                    {invoice?.description}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-text-primary">
                    ${invoice?.amount?.toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center space-x-1 text-xs font-medium px-2 py-1 rounded-full ${
                      invoice?.status === 'paid' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
                    }`}>
                      <Icon 
                        name={invoice?.status === 'paid' ? 'Check' : 'Clock'} 
                        size={12} 
                      />
                      <span className="capitalize">{invoice?.status}</span>
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadInvoice(invoice?.invoiceUrl)}
                      iconName="Download"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSection;